import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, CalendarDays, ShieldCheck } from "lucide-react";
import { fetchEarnings } from "@lawyer/services/lawyerApi";
import { rupees, fmtDate, StatePill, EarningStat, CompactStat, PAYOUT_STATUS_LABEL } from "@lawyer/components/earnings/EarningsPrimitives";
import { DateFilter } from "@lawyer/components/earnings/EarningsFilters";
import { EarningDetailDialog } from "@lawyer/components/earnings/EarningDetailDialog";

const MODE = { chat: "Chat", audio: "Audio", video: "Video" };
const NO_FILTER = { state: null, preset: "all", from: null, to: null };
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

export default function LawyerEarnings() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(NO_FILTER);
  const [busy, setBusy] = useState(false);

  const load = (f) => {
    setBusy(true);
    return fetchEarnings({ limit: 100, state: f.state || undefined, from: f.from || undefined, to: f.to || undefined })
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load earnings"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(NO_FILTER); }, []);

  const apply = ({ pending, ...next }) => { setFilters(next); if (!pending) load(next); };
  const toggleState = (st) => apply({ ...filters, state: filters.state === st ? null : st });
  const applyDate = ({ preset, from, to, pending }) => apply({ ...filters, preset, from, to, pending });
  const filtered = Boolean(filters.state || filters.from || filters.to);

  if (!data) return <div className="text-slate-500" data-testid="lawyer-earnings-loading">Loading…</div>;

  const { summary: s, items, payouts } = data;
  const n = s.counts;
  const completed = n.pending + n.available + n.processing + n.paid_out;
  const pill = (st, label, value, extra = {}) => (
    <CompactStat testid={`earn-${st.replace("_", "-")}`} label={label} value={value} active={filters.state === st} onClick={() => toggleState(st)} {...extra} />
  );

  return (
    <>
      <div className="mb-6" data-testid="lawyer-earnings-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Earnings</h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="lawyer-earnings-subtitle">
          Earnings become available {s.refund_window_hours} hours after consultation completion and are settled {s.settlement_cycle}.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <EarningStat testid="earn-total" label="Total Earned" value={rupees(s.total_earned_paise)} hint={plural(completed, "completed consultation")} accent="navy" />
        <EarningStat testid="earn-available-card" label="Available for Payout" value={rupees(s.available_paise)} hint={`${n.available} ready for the next ${s.settlement_cycle} settlement`} accent="emerald" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="earn-compact">
        <button type="button" onClick={() => apply({ ...filters, state: null })} aria-pressed={!filters.state} data-testid="earn-filter-all"
                className={`rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold transition-colors ${filters.state ? "bg-white border-slate-200 text-slate-500 hover:border-[#0A2540]/40" : "bg-[#0A2540] border-[#0A2540] text-white"}`}>All</button>
        {pill("pending", "Pending", rupees(s.pending_paise), { title: `${n.pending} in the ${s.refund_window_hours}h refund window` })}
        {pill("available", "Available", rupees(s.available_paise), { title: `${n.available} ready for the next ${s.settlement_cycle} settlement` })}
        {pill("processing", "Processing", rupees(s.processing_paise), { title: `${n.processing} in a payout being processed` })}
        {pill("paid_out", "Paid out", rupees(s.paid_out_paise), { title: `${n.paid_out} settled` })}
      </div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="earn-secondary">
        {pill("upcoming", "Upcoming", `${rupees(s.upcoming_paise)} · ${plural(n.upcoming, "consultation")}`, { title: "Booked, not completed yet — not earned until completed" })}
        {n.reversed > 0 && pill("reversed", "Reversed", `${rupees(s.reversed_paise)} · ${plural(n.reversed, "refunded consultation")}`, { tone: "rose" })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 flex items-center gap-1" data-testid="earnings-table-heading">
          <Wallet size={12} /> Consultation earnings
          {filtered && <span className="normal-case tracking-normal text-slate-500" data-testid="earnings-filtered-count">· {plural(data.filtered_items ?? items.length, "result")}</span>}
          {filtered && <button type="button" onClick={() => apply(NO_FILTER)} data-testid="earn-clear-filters" className="normal-case tracking-normal text-[#0A2540] underline underline-offset-2 ml-1">Clear filters</button>}
        </h2>
        <DateFilter key={filters.preset === "custom" ? "custom" : "preset"} preset={filters.preset} from={filters.from} to={filters.to} onApply={applyDate} />
      </div>
      <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden mb-8 transition-opacity ${busy ? "opacity-60" : ""}`} data-testid="earnings-table" aria-busy={busy}>
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="earnings-empty">
            {filtered ? "No earnings match these filters." : "No paid consultations yet."}
          </div>
        ) : (
          <>
            <ul className="md:hidden divide-y divide-slate-100" data-testid="earnings-cards">
              {items.map((it) => (
                <li key={it.consultation_id} onClick={() => setSelected(it)} data-testid={`earning-card-${it.consultation_id}`}
                    className="px-4 py-3 cursor-pointer hover:bg-slate-50 min-w-0">
                  <div className="flex items-baseline justify-between gap-3 min-w-0">
                    <span className="text-xs text-slate-500 truncate min-w-0">{fmtDate(it.date)} · <span className="font-mono">#{it.consultation_id.slice(-8).toUpperCase()}</span></span>
                    <span className="font-extrabold text-[#0A2540] shrink-0" data-testid={`earning-card-amount-${it.consultation_id}`}>{rupees(it.amount_paise)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-3 min-w-0">
                    <span className="text-xs text-slate-600 truncate min-w-0">{MODE[it.mode] || it.mode} · {it.duration_minutes} min</span>
                    <StatePill state={it.state} testid={`earning-card-state-${it.consultation_id}`} />
                  </div>
                </li>
              ))}
            </ul>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  {["Date", "Consultation", "Mode", "Duration", "Your Earnings", "Status"].map((h) => (
                    <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.consultation_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(it)} data-testid={`earning-row-${it.consultation_id}`}>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(it.date)}</td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-500">#{it.consultation_id.slice(-8).toUpperCase()}</td>
                    <td className="px-4 py-3 text-xs">{MODE[it.mode] || it.mode}</td>
                    <td className="px-4 py-3 text-xs">{it.duration_minutes} min</td>
                    <td className="px-4 py-3 font-extrabold text-[#0A2540]" data-testid={`earning-amount-${it.consultation_id}`}>{rupees(it.amount_paise)}</td>
                    <td className="px-4 py-3"><StatePill state={it.state} testid={`earning-state-${it.consultation_id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-1">
        <CalendarDays size={12} /> Payout history
      </h2>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-8" data-testid="payout-history">
        {payouts.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="payout-history-empty">
            No payouts yet. Available earnings are settled {s.settlement_cycle}.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                {["Date", "Amount", "Status", "Reference"].map((h) => (
                  <th key={h} className="text-left font-semibold px-4 py-3 text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.payout_id} className="border-t border-slate-100" data-testid={`payout-row-${p.payout_id}`}>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-3 font-extrabold text-[#0A2540]">{rupees(p.amount_paise)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                      p.status === "paid" ? "bg-emerald-100 text-emerald-800" : ["failed", "rejected", "reversed", "cancelled"].includes(p.status) ? "bg-rose-100 text-rose-800" : "bg-sky-100 text-sky-800"
                    }`} data-testid={`payout-status-${p.payout_id}`}>{PAYOUT_STATUS_LABEL[p.status] || p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-600">{p.payout_reference || p.payout_id}{p.provider_utr ? ` · UTR ${p.provider_utr}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3" data-testid="settlement-note">
        <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] grid place-items-center text-[#0A2540]"><ShieldCheck size={18} /></div>
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">How payouts work</p>
          <p className="mt-1 text-sm text-slate-700">
            When a consultation is completed you earn your professional fee. It stays <strong>Pending</strong> for {s.refund_window_hours} hours while the client's refund-request window is open, then becomes <strong>Available for Payout</strong>. Available earnings are settled {s.settlement_cycle} to your verified bank account and show here as <strong>Paid Out</strong>.
          </p>
        </div>
      </div>

      <EarningDetailDialog earning={selected} onClose={() => setSelected(null)} />
    </>
  );
}
